import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputAdornment,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { ArrowBack, CheckCircle, Print, Edit as EditIcon, Save, Cancel, Delete } from '@mui/icons-material';
import { customerInvoiceService } from '../services/customerInvoiceService';
import CurrencyTextField, { formatCurrency } from '../components/CurrencyTextField';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import ApplyCustomerCreditDialog from '../components/ApplyCustomerCreditDialog';
import { customerService } from '../services/customerService';
import PrintPage from '../components/print/PrintPage';
import { procurementSummaryFieldsRowSx } from '../components/procurement/ProcurementSummaryBar';
import { printWithFilename } from '../utils/printUtils';
import { effectiveServiceCode } from '../utils/serviceCodeDisplay';
import { DEFAULT_PAYMENT_TERMS } from '../constants/defaultPaymentTerms';
import { fetchPaymentTermsOptions } from '../services/paymentTermsService';
import { formatDateForInput, formatDateOnly, parseLocalDate, formatLocalTodayYmd } from '../utils/dateUtils';
import { formatQuantityDisplay } from '../utils/quantityDisplay';

function getTermsDays(terms) {
  if (!terms) return 0;
  const m = terms.match(/^Net\s+(\d+)$/i);
  if (m) return parseInt(m[1], 10) || 0;
  if (/^(Payable\s+On\s+Receipt|Pay\s+before\s+ship|Net\s*0)$/i.test(terms)) return 0;
  return 0;
}

function invoiceBillToLines(inv) {
  const l1 = inv.bill_to_address_line1 || inv.customer_address_line1 || inv.address_line1;
  const l2 = inv.bill_to_address_line2 || inv.customer_address_line2 || inv.address_line2;
  const city = inv.bill_to_city || inv.customer_city || inv.city;
  const state = inv.bill_to_state || inv.customer_state || inv.state;
  const zip = inv.bill_to_zip_code || inv.customer_zip_code || inv.zip_code;
  const country = inv.bill_to_country;
  const lines = [];
  if (l1) lines.push(l1);
  if (l2) lines.push(l2);
  const csz = [city, state, zip].filter(Boolean).join(', ');
  if (csz) lines.push(csz);
  if (country && country !== 'United States' && country !== 'USA') lines.push(country);
  return lines;
}

function invoiceShipToLines(inv) {
  const l1 = inv.ship_to_address_line1 || inv.packing_slip_ship_to_address_line1;
  const l2 = inv.ship_to_address_line2 || inv.packing_slip_ship_to_address_line2;
  const city = inv.ship_to_city || inv.packing_slip_ship_to_city;
  const state = inv.ship_to_state || inv.packing_slip_ship_to_state;
  const zip = inv.ship_to_zip_code || inv.packing_slip_ship_to_zip_code;
  const country = inv.ship_to_country || inv.packing_slip_ship_to_country;
  const lines = [];
  if (l1) lines.push(l1);
  if (l2) lines.push(l2);
  const csz = [city, state, zip].filter(Boolean).join(', ');
  if (csz) lines.push(csz);
  if (country && country !== 'United States' && country !== 'USA') lines.push(country);
  return lines;
}

function addDays(dateStr, days) {
  const d = parseLocalDate(dateStr);
  if (!d) return '';
  d.setDate(d.getDate() + (days || 0));
  return formatDateForInput(d);
}

const CustomerInvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidConfirmOpen, setMarkPaidConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [voidConfirmOpen, setVoidConfirmOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [reissuePromptOpen, setReissuePromptOpen] = useState(false);
  const [editingTerms, setEditingTerms] = useState(false);
  const [editPaymentTerms, setEditPaymentTerms] = useState('');
  const [editInvoiceDate, setEditInvoiceDate] = useState('');
  const [editDueDate, setEditDueDate] = useState('');
  const [editQuickbooksRef, setEditQuickbooksRef] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);
  const [paymentTermsOptions, setPaymentTermsOptions] = useState(DEFAULT_PAYMENT_TERMS);
  const [editingAmounts, setEditingAmounts] = useState(false);
  const [editShipping, setEditShipping] = useState('');
  const [editTax, setEditTax] = useState('');
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [editScheduleItems, setEditScheduleItems] = useState([]);
  const [activeScheduleRowIndex, setActiveScheduleRowIndex] = useState(null);
  const [inlineScheduleError, setInlineScheduleError] = useState({ rowIndex: null, message: '' });
  const [schedulePercentDrafts, setSchedulePercentDrafts] = useState({});
  const [scheduleUninvoicedAmount, setScheduleUninvoicedAmount] = useState(null);
  const [scheduleInvoiceCapTotal, setScheduleInvoiceCapTotal] = useState(null);
  const [scheduleOrderTotalAmount, setScheduleOrderTotalAmount] = useState(null);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [applyCreditOpen, setApplyCreditOpen] = useState(false);
  const [customerPrepaidBalance, setCustomerPrepaidBalance] = useState(0);

  const roundMoney = (n) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return 0;
    return Math.round(x * 100) / 100;
  };

  useEffect(() => {
    loadInvoice();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    fetchPaymentTermsOptions().then((list) => {
      if (!cancelled) setPaymentTermsOptions(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const invoiceTermsSelectOptions = useMemo(() => {
    const base = paymentTermsOptions;
    const t = editPaymentTerms;
    if (t && !base.includes(t)) {
      return [...base, t];
    }
    return base;
  }, [paymentTermsOptions, editPaymentTerms]);

  const canEditInvoiceAmounts =
    invoice &&
    !invoice.voided_at &&
    !invoice.paid_at &&
    parseFloat(invoice.amount_paid || 0) === 0;

  const startEditTerms = () => {
    const terms = invoice.payment_terms || 'Payable On Receipt';
    setEditPaymentTerms(terms);
    setEditInvoiceDate(formatDateForInput(invoice.invoice_date));
    setEditDueDate(formatDateForInput(invoice.due_date));
    setEditQuickbooksRef(invoice.quickbooks_ref || '');
    setEditingTerms(true);
  };

  const startEditAmounts = () => {
    setEditShipping(invoice.shipping_amount != null ? String(invoice.shipping_amount) : '');
    setEditTax(invoice.tax_amount != null ? String(invoice.tax_amount) : '');
    setEditingAmounts(true);
  };

  const startEditSchedule = async () => {
    const rows = Array.isArray(invoice?.schedule_items) ? invoice.schedule_items : [];
    try {
      if (invoice?.work_order_id) {
        const draft = await customerInvoiceService.getPartialInvoiceDraft(invoice.work_order_id);
        const d = draft?.data || {};
        const uninvoicedSub =
          d.uninvoiced_subtotal_amount != null && Number.isFinite(Number(d.uninvoiced_subtotal_amount))
            ? Number(d.uninvoiced_subtotal_amount)
            : Number(d.uninvoiced_amount);
        const currentSubtotal = Number(invoice.subtotal || 0);
        const orderTotal = Number(d.order_total_amount);
        if (Number.isFinite(uninvoicedSub) && Number.isFinite(currentSubtotal)) {
          setScheduleUninvoicedAmount(roundMoney(uninvoicedSub));
          // Cap is on billable subtotal: other invoices’ subtotals + this invoice’s max = order; draft uses uninvoiced_subtotal + this row’s subtotal.
          setScheduleInvoiceCapTotal(roundMoney(uninvoicedSub + currentSubtotal));
          setScheduleOrderTotalAmount(Number.isFinite(orderTotal) ? roundMoney(orderTotal) : null);
        } else {
          setScheduleUninvoicedAmount(null);
          setScheduleInvoiceCapTotal(null);
          setScheduleOrderTotalAmount(null);
        }
      }
    } catch {
      setScheduleUninvoicedAmount(null);
      setScheduleInvoiceCapTotal(null);
      setScheduleOrderTotalAmount(null);
    }
    setEditScheduleItems(
      rows.map((r) => ({
        id: r.id,
        label: r.label || '',
        amount: r.amount != null ? Number(r.amount) : '',
        due_date: formatDateForInput(r.due_date),
        _manualPctPrecision: false
      }))
    );
    setInlineScheduleError({ rowIndex: null, message: '' });
    setActiveScheduleRowIndex(0);
    setEditingSchedule(true);
  };

  const updateEditScheduleItem = (index, patch) => {
    setActiveScheduleRowIndex(index);
    if (inlineScheduleError.rowIndex === index) {
      setInlineScheduleError({ rowIndex: null, message: '' });
    }
    setEditScheduleItems((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const scheduleRowKey = (row, idx) => String(row?.id ?? `new-${idx}`);

  const addEditScheduleRow = () => {
    const today = formatDateForInput(new Date()) || new Date().toISOString().split('T')[0];
    setEditScheduleItems((prev) => {
      const currentSub = prev.reduce((s, r) => s + Math.max(0, Number(r.amount) || 0), 0);
      const maxScheduleSubtotal =
        scheduleInvoiceCapTotal != null ? Math.max(0, roundMoney(scheduleInvoiceCapTotal)) : null;
      const nextAmount = maxScheduleSubtotal != null
        ? Math.max(0, roundMoney(maxScheduleSubtotal - currentSub))
        : 0;
      const next = [...prev, { label: '', amount: nextAmount > 0 ? nextAmount : '', due_date: today, _manualPctPrecision: false }];
      setActiveScheduleRowIndex(next.length - 1);
      return next;
    });
  };

  const removeEditScheduleRow = (index) => {
    if (inlineScheduleError.rowIndex === index) {
      setInlineScheduleError({ rowIndex: null, message: '' });
    }
    setEditScheduleItems((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleSaveAmounts = async () => {
    if (!invoice?.id) return;
    try {
      setSavingAmounts(true);
      setError('');
      const ship = editShipping === '' ? 0 : parseFloat(editShipping);
      const tax = editTax === '' ? 0 : parseFloat(editTax);
      if (!Number.isFinite(ship) || ship < 0 || !Number.isFinite(tax) || tax < 0) {
        setError('Shipping and tax must be valid numbers ≥ 0.');
        return;
      }
      await customerInvoiceService.update(invoice.id, {
        shipping_amount: ship,
        tax_amount: tax
      });
      setEditingAmounts(false);
      loadInvoice();
    } catch (err) {
      console.error('Error updating invoice amounts:', err);
      setError(err.response?.data?.message || 'Failed to update shipping and tax');
    } finally {
      setSavingAmounts(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!invoice?.id) return;
    try {
      setSavingSchedule(true);
      setError('');
      if (scheduleOverCapAmount > 0.0001) {
        const msg = `Schedule exceeds allowed billable subtotal by ${formatCurrency(scheduleOverCapAmount, '$0.00')}. Reduce row amount(s).`;
        setError(msg);
        setInlineScheduleError({ rowIndex: activeScheduleRowIndex ?? 0, message: msg });
        return;
      }
      const normalized = editScheduleItems.map((row, i) => {
        const label = String(row.label || '').trim();
        const amount = Number(row.amount);
        const due = String(row.due_date || '').trim().split('T')[0];
        if (!label) throw new Error(`Schedule row ${i + 1}: label is required.`);
        if (!Number.isFinite(amount) || amount <= 0) throw new Error(`Schedule row ${i + 1}: amount must be greater than 0.`);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(due)) throw new Error(`Schedule row ${i + 1}: due date is required.`);
        return { ...(row.id ? { id: row.id } : {}), label, amount, due_date: due };
      });
      if (!normalized.length) throw new Error('At least one schedule row is required.');

      await customerInvoiceService.update(invoice.id, { schedule_items: normalized });
      setEditingSchedule(false);
      loadInvoice();
    } catch (err) {
      console.error('Error updating invoice schedule:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to update schedule';
      setError(msg);
      const rowMatch = String(msg).match(/Schedule row\s+(\d+)\s*:\s*(.+)$/i);
      if (rowMatch) {
        const idx = Math.max(0, (parseInt(rowMatch[1], 10) || 1) - 1);
        setInlineScheduleError({ rowIndex: idx, message: rowMatch[2] || msg });
        setActiveScheduleRowIndex(idx);
      } else if (activeScheduleRowIndex != null) {
        setInlineScheduleError({ rowIndex: activeScheduleRowIndex, message: msg });
      }
    } finally {
      setSavingSchedule(false);
    }
  };

  const scheduleDraftSubtotal = useMemo(
    () => roundMoney(editScheduleItems.reduce((s, r) => s + Math.max(0, Number(r.amount) || 0), 0)),
    [editScheduleItems]
  );

  const scheduleOverCapAmount = useMemo(() => {
    if (scheduleInvoiceCapTotal == null) return 0;
    return Math.max(0, roundMoney(scheduleDraftSubtotal - scheduleInvoiceCapTotal));
  }, [scheduleDraftSubtotal, scheduleInvoiceCapTotal]);

  const handleScheduleAmountChange = (rowIndex, value) => {
    let nextAmount = value === '' ? '' : Number(value);
    if (nextAmount !== '' && !Number.isFinite(nextAmount)) nextAmount = '';
    if (nextAmount !== '' && nextAmount < 0) nextAmount = 0;
    updateEditScheduleItem(rowIndex, {
      amount: nextAmount === '' ? '' : roundMoney(nextAmount),
      _manualPctPrecision: true
    });
  };

  const handleSchedulePercentChange = (rowIndex, value) => {
    const orderTotal = scheduleOrderTotalAmount;
    if (orderTotal == null || orderTotal <= 0) return;
    const pct = Number(value);
    if (!Number.isFinite(pct)) return;
    const bounded = Math.max(0, pct);
    const amount = roundMoney((orderTotal * bounded) / 100);
    updateEditScheduleItem(rowIndex, { amount, _manualPctPrecision: true });
  };

  const handleSchedulePercentDraftChange = (rowIndex, row, value) => {
    const key = scheduleRowKey(row, rowIndex);
    const nextRaw = String(value ?? '');
    if (nextRaw !== '' && !/^\d*\.?\d{0,2}$/.test(nextRaw)) return;
    setSchedulePercentDrafts((prev) => ({ ...prev, [key]: nextRaw }));
  };

  const handleSchedulePercentDraftFocus = (rowIndex, row) => {
    const key = scheduleRowKey(row, rowIndex);
    setSchedulePercentDrafts((prev) => ({ ...prev, [key]: getSchedulePercentDisplay(row) }));
    setActiveScheduleRowIndex(rowIndex);
  };

  const handleSchedulePercentDraftBlur = (rowIndex, row) => {
    const key = scheduleRowKey(row, rowIndex);
    const raw = schedulePercentDrafts[key];
    if (raw != null && raw !== '') {
      handleSchedulePercentChange(rowIndex, raw);
    }
    setSchedulePercentDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getSchedulePercentDisplay = (row) => {
    const orderTotal = scheduleOrderTotalAmount;
    const amt = Math.max(0, Number(row?.amount) || 0);
    if (orderTotal == null || orderTotal <= 0) return '';
    const pct = (amt / orderTotal) * 100;
    if (!Number.isFinite(pct)) return '';
    const roundedWhole = Math.round(pct);
    const isWhole = Math.abs(pct - roundedWhole) < 1e-9;
    if (row?._manualPctPrecision && !isWhole) return roundMoney(pct).toFixed(2);
    return String(roundedWhole);
  };

  const handleTermsChange = (newTerms) => {
    setEditPaymentTerms(newTerms);
    const days = getTermsDays(newTerms);
    if (editInvoiceDate && days >= 0) {
      setEditDueDate(addDays(editInvoiceDate, days));
    }
  };

  const handleInvoiceDateChange = (nextInvoiceDate) => {
    setEditInvoiceDate(nextInvoiceDate);
    const days = getTermsDays(editPaymentTerms);
    if (nextInvoiceDate && days >= 0) {
      setEditDueDate(addDays(nextInvoiceDate, days));
    }
  };

  const handleSaveTerms = async () => {
    if (!invoice?.id) return;
    try {
      setSavingTerms(true);
      setError('');
      const termsDays = getTermsDays(editPaymentTerms);
      await customerInvoiceService.update(invoice.id, {
        invoice_date: editInvoiceDate || null,
        payment_terms: editPaymentTerms,
        due_date: editDueDate || null,
        terms_days: termsDays,
        quickbooks_ref: String(editQuickbooksRef || '').trim() || null
      });
      setEditingTerms(false);
      loadInvoice();
    } catch (err) {
      console.error('Error updating terms:', err);
      setError(err.response?.data?.message || 'Failed to update terms');
    } finally {
      setSavingTerms(false);
    }
  };

  const loadInvoice = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await customerInvoiceService.getById(id);
      const inv = response.data;
      setInvoice(inv);
      if (inv?.customer_id) {
        try {
          const custRes = await customerService.getById(inv.customer_id);
          const credit =
            parseFloat(custRes?.data?.credit_available ?? custRes?.data?.prepaid_balance ?? 0) || 0;
          setCustomerPrepaidBalance(credit);
        } catch {
          setCustomerPrepaidBalance(0);
        }
      } else {
        setCustomerPrepaidBalance(0);
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError(err.response?.data?.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    try {
      setMarkingPaid(true);
      setError('');
      await customerInvoiceService.markPaid(id);
      setMarkPaidConfirmOpen(false);
      loadInvoice();
    } catch (err) {
      console.error('Error marking paid:', err);
      setError(err.response?.data?.message || 'Failed to mark invoice as paid');
    } finally {
      setMarkingPaid(false);
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      setDeleting(true);
      setError('');
      await customerInvoiceService.delete(id);
      setDeleteConfirmOpen(false);
      if (invoice?.work_order_id) {
        navigate(`/work-orders/${invoice.work_order_id}`, { replace: true });
      } else {
        navigate('/customer-invoices', { replace: true });
      }
    } catch (err) {
      console.error('Error deleting invoice:', err);
      setError(err.response?.data?.message || 'Failed to delete invoice');
    } finally {
      setDeleting(false);
    }
  };

  const handleVoidInvoice = async () => {
    try {
      setVoiding(true);
      setError('');
      await customerInvoiceService.void(id, { reason: voidReason || null });
      setVoidConfirmOpen(false);
      setVoidReason('');
      await loadInvoice();
      if (invoice?.work_order_id) {
        setReissuePromptOpen(true);
      }
    } catch (err) {
      console.error('Error voiding invoice:', err);
      setError(err.response?.data?.message || 'Failed to void invoice');
    } finally {
      setVoiding(false);
    }
  };

  const invoiceBalanceDue = (inv) => {
    if (inv?.voided_at) return 0;
    const total = Number(inv?.total_amount) || 0;
    const paid = Number(inv?.amount_paid) || 0;
    return Math.max(0, total - paid);
  };

  const invoiceIsPartiallyPaid = (inv) => {
    const total = Number(inv?.total_amount) || 0;
    const paid = Number(inv?.amount_paid) || 0;
    return total > 0 && paid > 0.02 && paid < total - 0.02;
  };

  const invoiceIsDueToday = (inv) => {
    const due = parseLocalDate(inv?.due_date);
    if (!due) return false;
    const today = parseLocalDate(formatLocalTodayYmd());
    if (!today) return false;
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return due.getTime() === today.getTime();
  };

  const invoiceStatusDisplay = (inv) => {
    const status = String(inv?.status || '');
    if (status === 'Voided' || inv?.voided_at) {
      return { label: 'Voided', sx: { bgcolor: 'grey.600', color: '#fff' } };
    }
    if (status === 'Paid' || invoiceBalanceDue(inv) <= 0.0001) {
      return { label: 'Paid', sx: { bgcolor: 'success.main', color: '#fff' } };
    }
    if (status === 'Past Due') {
      return { label: 'Past Due', sx: { bgcolor: 'error.main', color: '#fff' } };
    }
    if (invoiceIsDueToday(inv)) {
      return { label: 'Due today', sx: { bgcolor: 'warning.main', color: '#111' } };
    }
    return { label: 'Due', sx: { bgcolor: 'info.main', color: '#fff' } };
  };

  const renderInvoiceStatusChips = (inv) => {
    const main = invoiceStatusDisplay(inv);
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={main.label}
          size="small"
          sx={{ textTransform: 'none', fontWeight: 600, ...main.sx }}
        />
        {invoiceIsPartiallyPaid(inv) && String(inv?.status || '') !== 'Paid' ? (
          <Chip
            label="Partial"
            size="small"
            sx={{
              backgroundColor: '#ed6c02',
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
              '& .MuiChip-label': { px: 0.75 }
            }}
          />
        ) : null}
      </Box>
    );
  };

  const isScheduleInvoice =
    invoice != null && String(invoice.invoice_kind || 'line_items') === 'payment_schedule';

  const formatDate = (dateString) => {
    return formatDateOnly(dateString, 'MM/dd/yyyy') || '—';
  };

  const customerName =
    invoice?.company_name ||
    [invoice?.customer_first_name, invoice?.customer_last_name].filter(Boolean).join(' ') ||
    '—';
  const balanceDue = invoice
    ? (invoice.balance_due ?? (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)))
    : 0;
  const dueDateCallout = useMemo(() => {
    const due = parseLocalDate(invoice?.due_date);
    if (!due) return null;
    if ((Number(balanceDue) || 0) <= 0.0001) return null;
    const today = parseLocalDate(formatLocalTodayYmd());
    if (!today) return null;
    due.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    if (due.getTime() < today.getTime()) {
      return {
        severity: 'warning',
        message: `Past due: invoice was due on ${formatDateOnly(due, 'MM/dd/yyyy')}.`,
        chipLabel: 'PAST DUE',
        chipColor: 'error',
        chipVariant: 'filled'
      };
    }
    if (due.getTime() === today.getTime()) {
      return {
        severity: 'info',
        message: 'Due today: this invoice is due today.',
        chipLabel: 'DUE TODAY',
        chipColor: 'warning',
        chipVariant: 'filled'
      };
    }
    if (due.getTime() > today.getTime()) {
      return {
        severity: 'info',
        message: `Due ${formatDateOnly(due, 'MM/dd/yyyy')}: invoice has an outstanding balance.`,
        chipLabel: `DUE ${formatDateOnly(due, 'MM/dd')}`,
        chipColor: 'info',
        chipVariant: 'filled'
      };
    }
    return null;
  }, [invoice?.due_date, balanceDue]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invoice) {
    return (
      <Box>
        <Alert severity="error">Invoice not found</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customer-invoices')} sx={{ mt: 2 }}>
          Back to Invoices
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Print-friendly invoice view */}
      <PrintPage title="Invoice">
        <div className="print-quote-recipient" style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start' }}>
          <div style={{ minWidth: 200, flex: '1 1 200px' }}>
            <div style={{ marginBottom: 4 }}>
              <strong>Bill To</strong>
            </div>
            <div className="print-quote-to-address">
              <div>{customerName}</div>
              {invoiceBillToLines(invoice).map((line, i) => (
                <div key={`b-${i}`}>{line}</div>
              ))}
            </div>
          </div>
          <div style={{ minWidth: 200, flex: '1 1 200px' }}>
            <div style={{ marginBottom: 4 }}>
              <strong>Ship To</strong>
            </div>
            <div className="print-quote-to-address">
              {invoiceShipToLines(invoice).length ? (
                invoiceShipToLines(invoice).map((line, i) => <div key={`s-${i}`}>{line}</div>)
              ) : (
                <div style={{ color: '#666' }}>—</div>
              )}
            </div>
          </div>
        </div>

        <div className="print-quote-details-box" style={{ marginBottom: 16 }}>
          <div className="print-quote-details-left">
            <div><strong>Invoice #:</strong> {invoice.identifier ?? invoice.invoice_number}</div>
            <div>
              <strong>Invoice Date:</strong>{' '}
              {formatDate(invoice.invoice_date)}
            </div>
            {invoice.due_date && (
              <div>
                <strong>Due Date:</strong> {formatDate(invoice.due_date)}
              </div>
            )}
          </div>
          {invoice.packing_slip_id ? (
            <div className="print-quote-details-center">
              <div>
                <strong>Shipment No.</strong>{' '}
                {invoice.packing_slip_identifier ?? invoice.packing_slip_number ?? `#${invoice.packing_slip_id}`}
              </div>
              <div>
                <strong>Shipped On:</strong>{' '}
                {invoice.packing_slip_shipped_date ? formatDate(invoice.packing_slip_shipped_date) : '—'}
              </div>
            </div>
          ) : null}
          <div className="print-quote-details-right">
            <div><strong>Terms:</strong> {invoice.payment_terms || 'Payable On Receipt'}</div>
            <div><strong>Status:</strong> {invoice.status || 'open'}</div>
            {(invoice.status === 'Paid' || balanceDue <= 0.0001) && invoice.paid_late != null && (
              <div>
                <strong>Payment timing:</strong>{' '}
                {invoice.paid_late ? 'Paid after due date' : 'Paid on or before due date'}
              </div>
            )}
          </div>
        </div>

        <table className="print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
          {isScheduleInvoice ? (
            <>
              <thead>
                <tr>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Milestone</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Due</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Invoice Total</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Amount Paid</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.schedule_items || []).length > 0 ? (
                  (invoice.schedule_items || []).map((row) => (
                    <tr key={row.id}>
                      <td style={{ padding: '4px 6px', fontSize: '11px' }}>{row.label || '—'}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatDate(row.due_date)}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(row.amount, '—')}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(invoice.amount_paid, '—')}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px' }}>{row.schedule_row_status || '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>No schedule rows</td>
                  </tr>
                )}
              </tbody>
            </>
          ) : (
            <>
              <thead>
                <tr>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Item</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'left', backgroundColor: '#f0f0f0' }}>Description</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Qty</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Unit Price</th>
                  <th style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right', backgroundColor: '#f0f0f0' }}>Line Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).length > 0 ? (
                  (invoice.items || []).map((item) => (
                    <tr key={item.id}>
                      <td style={{ padding: '4px 6px', fontSize: '11px' }}>
                        {item.item_type === 'service' ? effectiveServiceCode(item) || '—' : item.part_number || '—'}
                      </td>
                      <td style={{ padding: '4px 6px', fontSize: '11px' }}>{item.description || item.part_name || item.service_name || '—'}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatQuantityDisplay(item.quantity ?? 0)}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(item.unit_price, '—')}</td>
                      <td style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'right' }}>{formatCurrency(item.line_total, '—')}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ padding: '4px 6px', fontSize: '11px', textAlign: 'center' }}>No line items</td>
                  </tr>
                )}
              </tbody>
            </>
          )}
        </table>

        <div className="print-quote-total-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
          {Number(invoice.discount_amount) > 0.005 && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Merchandise</span>
                <span>
                  {formatCurrency(
                    (Number(invoice.subtotal) || 0) + (Number(invoice.discount_amount) || 0),
                    '—'
                  )}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Discount</span>
                <span>−{formatCurrency(invoice.discount_amount, '—')}</span>
              </div>
            </>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Subtotal</span>
            <span>{formatCurrency(invoice.subtotal, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Shipping</span>
            <span>{formatCurrency(invoice.shipping_amount, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span>Tax</span>
            <span>{formatCurrency(invoice.tax_amount, '—')}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, marginTop: 8, paddingTop: 8, borderTop: '1px solid #ccc' }}>
            <strong>Total</strong>
            <span>{formatCurrency(invoice.total_amount, '—')}</span>
          </div>
        </div>
      </PrintPage>
      <Box className="no-print">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/customer-invoices')} title="Back">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Invoice {invoice.identifier ?? invoice.invoice_number}
          </Typography>
          {renderInvoiceStatusChips(invoice)}
          {(invoice.status === 'Paid' || balanceDue <= 0.0001) && invoice.paid_late === true && (
            <Chip label="Late payment" color="warning" size="small" sx={{ fontWeight: 600 }} />
          )}
          {(invoice.status === 'Paid' || balanceDue <= 0.0001) && invoice.paid_late === false && (
            <Chip label="Paid on time" color="success" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!invoice.voided_at && balanceDue > 0 && invoice.customer_id && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setRecordPaymentOpen(true)}
            >
              Record Payment
            </Button>
          )}
          {!invoice.voided_at && balanceDue > 0 && invoice.customer_id && customerPrepaidBalance > 0.01 && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setApplyCreditOpen(true)}
            >
              Apply Credit ({formatCurrency(customerPrepaidBalance)})
            </Button>
          )}
          {!invoice.voided_at && balanceDue > 0 && (
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<CheckCircle />}
              onClick={() => setMarkPaidConfirmOpen(true)}
            >
              Mark Paid
            </Button>
          )}
          <IconButton size="small" onClick={() => printWithFilename(invoice?.identifier || invoice?.invoice_number || 'Invoice')} title="Print">
            <Print />
          </IconButton>
          {!invoice.voided_at && balanceDue > 0 && (
            <Button
              variant="outlined"
              color="warning"
              size="small"
              onClick={() => setVoidConfirmOpen(true)}
              disabled={voiding}
            >
              Void invoice
            </Button>
          )}
          {!invoice.voided_at && (
            <IconButton
              size="small"
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              title="Permanently delete invoice (prefer Void)"
              disabled={deleting}
            >
              <Delete />
            </IconButton>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}
      {invoice.voided_at && (
        <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>
          Voided — not payable
          {invoice.void_reason ? ` (${invoice.void_reason})` : ''}
          {invoice.voided_at
            ? ` · ${new Date(invoice.voided_at).toLocaleString()}`
            : ''}
          {(invoice.voided_by_first_name || invoice.voided_by_last_name)
            ? ` · ${[invoice.voided_by_first_name, invoice.voided_by_last_name].filter(Boolean).join(' ')}`
            : ''}
          {invoice.superseded_by_invoice_id ? (
            <>
              {' · '}
              <Button
                size="small"
                sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline', textTransform: 'none' }}
                onClick={() => navigate(`/customer-invoices/${invoice.superseded_by_invoice_id}`)}
              >
                View replacement invoice
              </Button>
            </>
          ) : null}
        </Alert>
      )}
      {dueDateCallout && !invoice.voided_at && (
        <Alert severity={dueDateCallout.severity} sx={{ mb: 1.5, py: 0.5 }}>
          {dueDateCallout.message}
        </Alert>
      )}

      <Box sx={{ p: 1.5, mb: 1.5, backgroundColor: 'surfaceMuted.main', border: 1, borderColor: 'borderSubtle.main', borderRadius: 1 }}>
        <Box sx={procurementSummaryFieldsRowSx()}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {invoice.identifier ?? invoice.invoice_number ?? '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {invoice.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${invoice.customer_id}`)}
                >
                  {customerName}
                </Button>
              ) : (
                customerName
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order #</Typography>
            <Typography variant="body2">
              {invoice.work_order_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders/${invoice.work_order_id}`)}
                >
                  {invoice.work_order_identifier ?? invoice.work_order_number ?? `#${invoice.work_order_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer PO</Typography>
            <Typography variant="body2">{invoice.po_number || '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Packing Slip #</Typography>
            <Typography variant="body2">
              {invoice.packing_slip_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/packing-slips/${invoice.packing_slip_id}`)}
                >
                  {invoice.packing_slip_identifier ?? invoice.packing_slip_number ?? `#${invoice.packing_slip_id}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice Date</Typography>
            <Typography variant="body2">{formatDate(invoice.invoice_date)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Due Date</Typography>
            <Typography variant="body2">{formatDate(invoice.due_date)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Created by</Typography>
            <Typography variant="body2">
              {[invoice.created_by_first_name, invoice.created_by_last_name].filter(Boolean).join(' ').trim() || '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Total</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(invoice.total_amount, '—')}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Status</Typography>
            {renderInvoiceStatusChips(invoice)}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>QuickBooks Ref #</Typography>
            <Typography variant="body2">{invoice.quickbooks_ref || '—'}</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 1.5, mb: 1.5, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Bill to
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {invoiceBillToLines(invoice).join('\n') || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Ship to
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {invoiceShipToLines(invoice).join('\n') || '—'}
            </Typography>
            {invoice.packing_slip_id ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Shipment invoices use the packing slip ship-to when recorded.
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent sx={{ pb: '10px !important' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Invoice Details
                </Typography>
                {!editingTerms ? (
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditTerms}>
                    Edit terms & due date
                  </Button>
                ) : null}
              </Box>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice #
                  </Typography>
                  <Typography variant="body2">{invoice.identifier ?? invoice.invoice_number}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Invoice Date
                  </Typography>
                  {editingTerms ? (
                    <TextField
                      size="small"
                      type="date"
                      value={editInvoiceDate}
                      onChange={(e) => handleInvoiceDateChange(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'aria-label': 'Invoice date' }}
                    />
                  ) : (
                    <Typography variant="body2">{formatDate(invoice.invoice_date)}</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Due Date
                  </Typography>
                  {editingTerms ? (
                    <TextField
                      size="small"
                      type="date"
                      value={editDueDate}
                      onChange={(e) => setEditDueDate(e.target.value)}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ 'aria-label': 'Due date' }}
                    />
                  ) : (
                    <Typography variant="body2">{formatDate(invoice.due_date)}</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    Terms
                  </Typography>
                  {editingTerms ? (
                    <FormControl fullWidth size="small">
                      <Select
                        value={editPaymentTerms}
                        onChange={(e) => handleTermsChange(e.target.value)}
                        displayEmpty
                        inputProps={{ 'aria-label': 'Payment terms' }}
                      >
                        {invoiceTermsSelectOptions.map((opt) => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Typography variant="body2">{invoice.payment_terms || '—'}</Typography>
                  )}
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">
                    QuickBooks Ref #
                  </Typography>
                  {editingTerms ? (
                    <TextField
                      size="small"
                      value={editQuickbooksRef}
                      onChange={(e) => setEditQuickbooksRef(e.target.value)}
                      fullWidth
                      inputProps={{ maxLength: 100, 'aria-label': 'QuickBooks reference number' }}
                    />
                  ) : (
                    <Typography variant="body2">{invoice.quickbooks_ref || '—'}</Typography>
                  )}
                </Grid>
                {editingTerms && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSaveTerms} disabled={savingTerms}>
                        {savingTerms ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" startIcon={<Cancel />} onClick={() => setEditingTerms(false)} disabled={savingTerms}>
                        Cancel
                      </Button>
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12} sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body2">{customerName}</Typography>
                </Grid>
                <Grid item xs={12} sx={{ mt: 0.25 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => navigate(`/work-orders/${invoice.work_order_id}`)}
                  >
                    View Order #{invoice.work_order_identifier ?? invoice.work_order_number}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                {isScheduleInvoice ? 'Payment schedule' : 'Line Items'}
              </Typography>
              {isScheduleInvoice && (
                <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                  Row status uses earliest due installments first (FIFO) based on recorded payments on this invoice.
                </Alert>
              )}
              <TableContainer>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': { py: 0.5, px: 0.75, fontSize: '0.8125rem' },
                    '& .MuiTableCell-head': { py: 0.5, fontSize: '0.75rem' }
                  }}
                >
                  {isScheduleInvoice ? (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          {editingSchedule ? (
                            <>
                              <TableCell sx={{ fontWeight: 600 }}>Milestone</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Due
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Invoice Total
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Amount Paid
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, minWidth: 110 }}>
                                %
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Payment Date
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Applied On
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>
                                Amount Paid
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Pmt ID</TableCell>
                            </>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editingSchedule ? (
                          (editScheduleItems || []).map((row, idx) => (
                            <TableRow key={row.id ?? `draft-row-${idx}`}>
                              <TableCell>
                                <TextField
                                  size="small"
                                  fullWidth
                                  value={row.label || ''}
                                  onChange={(e) => updateEditScheduleItem(idx, { label: e.target.value })}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <TextField
                                  size="small"
                                  type="date"
                                  value={formatDateForInput(row.due_date)}
                                  onChange={(e) => updateEditScheduleItem(idx, { due_date: e.target.value })}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <CurrencyTextField
                                  size="small"
                                  value={row.amount}
                                  onChange={(e) => handleScheduleAmountChange(idx, e.target.value)}
                                  minimumFractionDigits={2}
                                  maximumFractionDigits={2}
                                  dollarAdornment
                                  error={scheduleOverCapAmount > 0.0001}
                                />
                              </TableCell>
                              <TableCell align="right">
                                {formatCurrency(invoice.amount_paid, '—')}
                              </TableCell>
                              <TableCell align="right" sx={{ minWidth: 110 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={schedulePercentDrafts[scheduleRowKey(row, idx)] ?? getSchedulePercentDisplay(row)}
                                  onFocus={() => handleSchedulePercentDraftFocus(idx, row)}
                                  onBlur={() => handleSchedulePercentDraftBlur(idx, row)}
                                  onChange={(e) => handleSchedulePercentDraftChange(idx, row, e.target.value)}
                                  inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                                  InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                                  }}
                                  error={scheduleOverCapAmount > 0.0001}
                                  sx={{ width: 120 }}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                                  <Button size="small" color="error" onClick={() => removeEditScheduleRow(idx)} disabled={editScheduleItems.length <= 1}>
                                    Remove
                                  </Button>
                                  {inlineScheduleError.rowIndex === idx && inlineScheduleError.message ? (
                                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                                      {inlineScheduleError.message}
                                    </Typography>
                                  ) : null}
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (invoice.payments_applied || []).length > 0 ? (
                          (invoice.payments_applied || []).map((p) => (
                            <TableRow key={p.id}>
                              <TableCell>Payment applied</TableCell>
                              <TableCell align="right">{formatDate(p.payment_date)}</TableCell>
                              <TableCell align="right">{formatDate(p.created_at)}</TableCell>
                              <TableCell align="right">{formatCurrency(p.amount_applied, '—')}</TableCell>
                              <TableCell>
                                {p.payment_id ? (
                                  <Button
                                    size="small"
                                    variant="text"
                                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    onClick={() => navigate(`/customer-payments/${p.payment_id}`)}
                                  >
                                    {p.payment_number || `#${p.payment_id}`}
                                  </Button>
                                ) : (
                                  p.payment_number || '—'
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              No payments applied
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </>
                  ) : (
                    <>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Item</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Qty
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Unit Price
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            Line Total
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(invoice.items || []).map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>{item.item_type === 'service' ? 'Service' : 'Part'}</TableCell>
                            <TableCell>
                              {item.item_type === 'service' ? effectiveServiceCode(item) || '—' : item.part_number || '—'}
                            </TableCell>
                            <TableCell>{item.description || item.part_name || item.service_name || '—'}</TableCell>
                            <TableCell align="right">{formatQuantityDisplay(item.quantity ?? 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.unit_price, '—')}</TableCell>
                            <TableCell align="right">{formatCurrency(item.line_total, '—')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </>
                  )}
                </Table>
              </TableContainer>
              {isScheduleInvoice && !editingSchedule && (
                <Box
                  sx={{
                    mt: 1.25,
                    ml: 'auto',
                    width: { xs: '100%', sm: 360 },
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    pt: 1
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Total payments applied</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(invoice.amount_paid, '—')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Invoice total</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(invoice.total_amount, '—')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2">Outstanding balance</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatCurrency(invoice.balance_due ?? (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)), '—')}
                    </Typography>
                  </Box>
                </Box>
              )}
              {isScheduleInvoice && !editingSchedule && canEditInvoiceAmounts && (
                <Box sx={{ mt: 1.25, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditSchedule}>
                    Edit payment schedule
                  </Button>
                </Box>
              )}
              {isScheduleInvoice && editingSchedule && (
                <Box sx={{ mt: 1.25, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  {scheduleUninvoicedAmount != null ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', alignSelf: 'center' }}>
                      {scheduleOrderTotalAmount != null ? `Order sell total: ${formatCurrency(scheduleOrderTotalAmount, '$0.00')} · ` : ''}
                      Uninvoiced subtotal: {formatCurrency(scheduleUninvoicedAmount, '$0.00')}
                      {scheduleInvoiceCapTotal != null
                        ? ` · Max billable subtotal (this invoice): ${formatCurrency(scheduleInvoiceCapTotal, '$0.00')}`
                        : ''}
                      {scheduleOverCapAmount > 0.0001
                        ? ` · Over by ${formatCurrency(scheduleOverCapAmount, '$0.00')}`
                        : ''}
                    </Typography>
                  ) : null}
                  <Button size="small" onClick={addEditScheduleRow}>Add row</Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSaveSchedule}
                    disabled={savingSchedule || scheduleOverCapAmount > 0.0001}
                    color={scheduleOverCapAmount > 0.0001 ? 'error' : 'primary'}
                  >
                    {savingSchedule ? 'Saving...' : 'Save schedule'}
                  </Button>
                  <Button
                    size="small"
                    startIcon={<Cancel />}
                    onClick={() => {
                      setEditingSchedule(false);
                      setEditScheduleItems([]);
                      setInlineScheduleError({ rowIndex: null, message: '' });
                      setActiveScheduleRowIndex(null);
                    }}
                    disabled={savingSchedule}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Totals
                </Typography>
                {!editingAmounts && canEditInvoiceAmounts ? (
                  <Button size="small" startIcon={<EditIcon />} onClick={startEditAmounts}>
                    Edit shipping &amp; tax
                  </Button>
                ) : null}
              </Box>
              {Number(invoice.discount_amount) > 0.005 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Merchandise</Typography>
                    <Typography variant="body2">
                      {formatCurrency(
                        (Number(invoice.subtotal) || 0) + (Number(invoice.discount_amount) || 0),
                        '—'
                      )}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">Discount</Typography>
                    <Typography variant="body2">−{formatCurrency(invoice.discount_amount, '—')}</Typography>
                  </Box>
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">{formatCurrency(invoice.subtotal, '—')}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                <Typography variant="body2">Shipping</Typography>
                {editingAmounts ? (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'aria-label': 'Shipping amount' }}
                    value={editShipping}
                    onChange={(e) => setEditShipping(e.target.value)}
                    sx={{ width: 120 }}
                  />
                ) : (
                  <Typography variant="body2">{formatCurrency(invoice.shipping_amount, '—')}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5, alignItems: 'center' }}>
                <Typography variant="body2">Tax</Typography>
                {editingAmounts ? (
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, step: '0.01', 'aria-label': 'Tax amount' }}
                    value={editTax}
                    onChange={(e) => setEditTax(e.target.value)}
                    sx={{ width: 120 }}
                  />
                ) : (
                  <Typography variant="body2">{formatCurrency(invoice.tax_amount, '—')}</Typography>
                )}
              </Box>
              {editingAmounts && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSaveAmounts} disabled={savingAmounts}>
                    {savingAmounts ? 'Saving...' : 'Save amounts'}
                  </Button>
                  <Button size="small" startIcon={<Cancel />} onClick={() => setEditingAmounts(false)} disabled={savingAmounts}>
                    Cancel
                  </Button>
                </Box>
              )}
              {!canEditInvoiceAmounts && (invoice.paid_at || parseFloat(invoice.amount_paid || 0) > 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Shipping and tax cannot be changed after payment is recorded.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1.5,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Total
                </Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {formatCurrency(invoice.total_amount, '—')}
                </Typography>
              </Box>
              {(invoice.amount_paid || 0) > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9375rem' }}>
                    Amount paid: {formatCurrency(invoice.amount_paid)}
                  </Typography>
                  {(invoice.balance_due ?? parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)) > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mt: 0.5
                      }}
                    >
                      <Typography variant="body2" color="warning.main" sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>
                        Balance due: {formatCurrency(invoice.balance_due ?? (parseFloat(invoice.total_amount || 0) - parseFloat(invoice.amount_paid || 0)))}
                      </Typography>
                      {dueDateCallout?.chipLabel ? (
                        <Chip
                          label={dueDateCallout.chipLabel}
                          color={dueDateCallout.chipColor || 'default'}
                          variant={dueDateCallout.chipVariant || 'filled'}
                          size="small"
                          sx={{
                            flexShrink: 0,
                            height: 28,
                            minHeight: 28,
                            '& .MuiChip-label': {
                              px: 1.25,
                              py: 0.25,
                              lineHeight: 1.2,
                              fontSize: '0.8125rem',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase'
                            }
                          }}
                        />
                      ) : null}
                    </Box>
                  )}
                </Box>
              )}
              {invoice.paid_at && (
                <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1 }}>
                  Paid on {formatDate(invoice.paid_at)}
                </Typography>
              )}
              {(invoice.payments_applied || []).length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                    Payments applied
                  </Typography>
                  {(invoice.payments_applied || []).map((p) => (
                    <Typography key={p.id} variant="caption" sx={{ display: 'block' }}>
                      {p.payment_id ? (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ p: 0, minWidth: 'auto', textTransform: 'none', verticalAlign: 'baseline' }}
                          onClick={() => navigate(`/customer-payments/${p.payment_id}`)}
                        >
                          {p.payment_number || `#${p.payment_id}`}
                        </Button>
                      ) : (
                        p.payment_number || '—'
                      )}
                      {' '}
                      ({formatDate(p.payment_date)}): {formatCurrency(p.amount_applied)}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      </Box>

      <Dialog open={markPaidConfirmOpen} onClose={() => setMarkPaidConfirmOpen(false)}>
        <DialogTitle>Mark Invoice as Paid</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to mark invoice {invoice.identifier ?? invoice.invoice_number} as paid?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMarkPaidConfirmOpen(false)} disabled={markingPaid}>
            Cancel
          </Button>
          <Button onClick={handleMarkPaid} color="success" variant="contained" disabled={markingPaid}>
            {markingPaid ? 'Saving...' : 'Mark Paid'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !deleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Permanently delete invoice {invoice.identifier ?? invoice.invoice_number}? Prefer Void to keep an audit trail.
            Packing slips are not deleted. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteInvoice} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete permanently'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={voidConfirmOpen} onClose={() => !voiding && setVoidConfirmOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Void Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Void invoice {invoice.identifier ?? invoice.invoice_number}? It will be kept for audit but will not be payable,
            and its quantities will be available to reissue.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={voidReason}
            onChange={(e) => setVoidReason(e.target.value)}
            disabled={voiding}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVoidConfirmOpen(false)} disabled={voiding}>
            Cancel
          </Button>
          <Button onClick={handleVoidInvoice} color="warning" variant="contained" disabled={voiding}>
            {voiding ? 'Voiding...' : 'Void invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reissuePromptOpen} onClose={() => setReissuePromptOpen(false)}>
        <DialogTitle>Create replacement invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The invoice was voided. Create a replacement now so it links as the successor?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReissuePromptOpen(false)}>Not now</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                setReissuePromptOpen(false);
                setError('');
                if (String(invoice.invoice_kind || '') === 'payment_schedule') {
                  if (invoice.work_order_id) {
                    navigate(`/work-orders/${invoice.work_order_id}`);
                  }
                  return;
                }
                let created;
                if (invoice.packing_slip_id) {
                  const res = await customerInvoiceService.createFromPackingSlip(invoice.packing_slip_id, {
                    supersedes_invoice_id: invoice.id
                  });
                  created = res?.data;
                } else if (invoice.work_order_id) {
                  const draftRes = await customerInvoiceService.getPartialInvoiceDraft(invoice.work_order_id);
                  const draftLines = Array.isArray(draftRes?.data?.draft_lines) ? draftRes.data.draft_lines : [];
                  const lines = draftLines
                    .map((row) => {
                      const remaining = Number(row.quantity_remaining ?? row.quantity ?? 0);
                      if (!(remaining > 0)) return null;
                      return {
                        work_order_item_id: row.work_order_item_id,
                        quantity: remaining
                      };
                    })
                    .filter(Boolean);
                  if (lines.length === 0) {
                    setError('No remaining quantity to reissue. Create an invoice from the order when ready.');
                    return;
                  }
                  const res = await customerInvoiceService.createFromWorkOrderRemaining(invoice.work_order_id, {
                    lines,
                    supersedes_invoice_id: invoice.id
                  });
                  created = res?.data;
                }
                if (created?.id) {
                  navigate(`/customer-invoices/${created.id}`, { replace: true });
                }
              } catch (err) {
                console.error('Error creating replacement invoice:', err);
                setError(err.response?.data?.message || 'Failed to create replacement invoice');
              }
            }}
          >
            Create replacement
          </Button>
        </DialogActions>
      </Dialog>

      <RecordPaymentDialog
        open={recordPaymentOpen}
        onClose={() => setRecordPaymentOpen(false)}
        onSuccess={loadInvoice}
        title={`Record Payment for ${invoice?.identifier ?? invoice?.invoice_number ?? `#${invoice?.id}`}`}
        initialCustomerId={invoice?.customer_id}
        initialWorkOrderId={invoice?.work_order_id}
        initialWorkOrderLabel={invoice?.work_order_identifier ?? invoice?.work_order_number ?? `#${invoice?.work_order_id}`}
        initialInvoiceId={invoice?.id}
        initialAmount={balanceDue > 0 ? Number(balanceDue).toFixed(2) : ''}
        lockCustomer
        lockWorkOrder={false}
      />

      <ApplyCustomerCreditDialog
        open={applyCreditOpen}
        onClose={() => setApplyCreditOpen(false)}
        onSuccess={loadInvoice}
        customerId={invoice?.customer_id}
        invoiceId={invoice?.id}
        balanceDue={balanceDue}
        prepaidBalance={customerPrepaidBalance}
      />
    </Box>
  );
};

export default CustomerInvoiceDetail;
